import React from 'react';
import { isRichTextEmpty } from '../utils/richtext';
import { Box, Button, IconButton, Stack, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import type { MultilingualString, StationTipLink, StationTips } from '../api/types';
import MultilingualField from './MultilingualField';

interface StationTipsFieldProps {
  value: StationTips | null;
  onChange: (next: StationTips | null) => void;
  disabled?: boolean;
}

const MAX_LINKS = 10;

const isValidUrl = (url: string) => /^https?:\/\//i.test(url);

/**
 * Creator editor for a station's optional tips & links popup (#262).
 * Participants see this as a dialog they can open from the station screen —
 * see StationTipsDialog for the read-only counterpart.
 *
 * The header + links are collapsed to `null` the moment both are empty, so
 * a station that was never given tips submits `tips: null` rather than an
 * empty-but-present object.
 */
const StationTipsField: React.FC<StationTipsFieldProps> = ({ value, onChange, disabled = false }) => {
  const { t } = useTranslation('content');

  const header: MultilingualString = value?.header ?? {};
  const links: StationTipLink[] = value?.links ?? [];

  const emitOrClear = (nextHeader: MultilingualString, nextLinks: StationTipLink[]) => {
    const stillHasHeader = Object.values(nextHeader).some((v) => !isRichTextEmpty(v));
    if (!stillHasHeader && nextLinks.length === 0) {
      onChange(null);
      return;
    }
    onChange({ header: nextHeader, links: nextLinks });
  };

  const handleHeaderChange = (nextHeader: MultilingualString) => {
    emitOrClear(nextHeader, links);
  };

  const handleAddLink = () => {
    if (links.length >= MAX_LINKS) return;
    emitOrClear(header, [...links, { label: {}, url: '' }]);
  };

  const handleRemoveLink = (idx: number) => {
    emitOrClear(header, links.filter((_, i) => i !== idx));
  };

  const handleLabelChange = (idx: number, nextLabel: MultilingualString) => {
    const nextLinks = links.map((link, i) => (i === idx ? { ...link, label: nextLabel } : link));
    emitOrClear(header, nextLinks);
  };

  const handleUrlChange = (idx: number, nextUrl: string) => {
    const nextLinks = links.map((link, i) => (i === idx ? { ...link, url: nextUrl } : link));
    emitOrClear(header, nextLinks);
  };

  return (
    <Box>
      <Typography variant="subtitle2" component="div" sx={{ mb: 0.5 }}>
        {t('station.tips.sectionTitle')}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        {t('station.tips.helper')}
      </Typography>

      <Stack spacing={2}>
        <MultilingualField
          value={header}
          onChange={handleHeaderChange}
          label={t('station.tips.headerLabel')}
          id="station-tips-header"
          richText
        />

        {links.map((link, idx) => {
          const urlInvalid = !!link.url.trim() && !isValidUrl(link.url);
          const removeLabel = t('station.tips.removeLink', {
            label: link.label.de || `${idx + 1}`,
          });
          return (
            <Stack
              key={idx}
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { xs: 'stretch', sm: 'flex-start' } }}
            >
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <MultilingualField
                  value={link.label}
                  onChange={(next) => handleLabelChange(idx, next)}
                  label={t('station.tips.linkLabel', { index: idx + 1 })}
                  id={`station-tips-link-label-${idx}`}
                />
              </Box>
              <TextField
                label={t('station.tips.urlLabel')}
                value={link.url}
                onChange={(e) => handleUrlChange(idx, e.target.value)}
                error={urlInvalid}
                helperText={urlInvalid ? t('station.tips.urlInvalid') : undefined}
                fullWidth
                size="small"
                disabled={disabled}
                sx={{ flex: 1 }}
              />
              <IconButton
                aria-label={removeLabel}
                onClick={() => handleRemoveLink(idx)}
                disabled={disabled}
                size="small"
                sx={{ mt: { xs: 0, sm: 1 } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          );
        })}

        <Button
          startIcon={<AddIcon />}
          onClick={handleAddLink}
          size="small"
          variant="outlined"
          disabled={disabled || links.length >= MAX_LINKS}
          sx={{ alignSelf: 'flex-start' }}
        >
          {t('station.tips.addLink')}
        </Button>
      </Stack>
    </Box>
  );
};

export default StationTipsField;
